import React, { useState, useEffect } from "react";
import "./styles.css";

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav className={`nav${scrolled ? " nav-scrolled" : ""}`}>
      <div className="nav-inner">
        <a href="/" className="nav-brand">MSS</a>
        <ul className="nav-links">
          <li><a href="#about">About</a></li>
          <li><a href="#services">Services</a></li>
          <li><a href="#portfolio">Portfolio</a></li>
          <li><a href="#contact" className="nav-cta">Contact</a></li>
        </ul>
      </div>
    </nav>
  );
};

const Hero = () => (
  <section className="hero">
    <div className="hero-glow" />
    <div className="hero-inner">
      <span className="hero-badge">&#10003;&nbsp; Available for Projects</span>
      <h1>McCarthy Software <span className="gradient-text">Solutions</span></h1>
      <p className="hero-tagline">Cloud &amp; Infrastructure Consulting</p>
      <p className="hero-desc">
        Building scalable AWS infrastructure, automating DevOps workflows, and delivering
        AI-driven solutions for modern businesses.
      </p>
      <div className="hero-btns">
        <a href="#contact" className="btn btn-primary">Get In Touch</a>
        <a href="#services" className="btn btn-ghost">Explore Services</a>
      </div>
    </div>
  </section>
);

const About = () => (
  <section id="about" className="section">
    <div className="section-inner">
      <h2 className="section-title">About</h2>
      <div className="about-content">
        <div className="about-avatar">
          <div className="avatar-circle">RM</div>
        </div>
        <div className="about-text">
          <h3>Ryan McCarthy</h3>
          <p className="about-role">Software Engineer &nbsp;·&nbsp; AWS Certified &nbsp;·&nbsp; DevOps &amp; AI Specialist</p>
          <p>
            A software engineer with expertise in cloud infrastructure, DevOps, and AI development.
            With a Bachelor of Engineering (Hons) in Software Engineering and multiple AWS certifications,
            I specialise in designing scalable cloud architectures, infrastructure automation, and
            AI-driven solutions.
          </p>
          <p>
            I have built edge inferencing systems, multi-region video processing pipelines, and
            modernised legacy .NET applications. Whether you need cloud solutions, automation, or AI
            integrations, I can help streamline your workflows and optimise your infrastructure.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const serviceItems = [
  {
    icon: "☁",
    title: "AWS Cloud Architecture",
    desc: "Design and deploy secure, scalable, multi-region cloud infrastructure tailored to your business needs.",
  },
  {
    icon: "⚙",
    title: "Infrastructure as Code",
    desc: "Automate your infrastructure with Terraform, AWS CDK, and CloudFormation for repeatable, reliable deployments.",
  },
  {
    icon: "🔗",
    title: "API Development & Integration",
    desc: "Build robust APIs and seamlessly integrate third-party services to power your applications.",
  },
  {
    icon: "🚀",
    title: "DevOps & Automation",
    desc: "Streamline development with CI/CD pipelines, Docker, Kubernetes, and serverless architectures.",
  },
  {
    icon: "🤖",
    title: "AI & Machine Learning",
    desc: "Implement intelligent solutions with TensorFlow, PyTorch, and custom computer vision applications.",
  },
  {
    icon: "💻",
    title: "Software Development",
    desc: "Build modern applications with Python, C#, and .NET with a focus on performance and maintainability.",
  },
];

const Services = () => (
  <section id="services" className="section section-alt">
    <div className="section-inner">
      <h2 className="section-title">Services</h2>
      <p className="section-sub">Comprehensive solutions from cloud architecture to AI development</p>
      <div className="services-grid">
        {serviceItems.map((s) => (
          <div key={s.title} className="service-card">
            <div className="service-icon">{s.icon}</div>
            <h3>{s.title}</h3>
            <p>{s.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const techStack = [
  "AWS", "Terraform", "AWS CDK", "CloudFormation",
  "Docker", "Kubernetes", "Python", "C# / .NET",
  "TensorFlow", "PyTorch", "CI/CD", "Serverless",
];

const TechStack = () => (
  <section className="section">
    <div className="section-inner">
      <h2 className="section-title">Tech Stack</h2>
      <p className="section-sub">Technologies I work with day-to-day</p>
      <div className="tech-grid">
        {techStack.map((t) => (
          <span key={t} className="tech-badge">{t}</span>
        ))}
      </div>
    </div>
  </section>
);

const Portfolio = () => (
  <section id="portfolio" className="section section-alt">
    <div className="section-inner">
      <h2 className="section-title">Portfolio</h2>
      <p className="section-sub">Selected work and projects</p>
      <div className="portfolio-placeholder">
        <div className="portfolio-icon">📁</div>
        <h3>Projects Coming Soon</h3>
        <p>
          A showcase of cloud architectures, infrastructure automation projects, and AI
          implementations is in the works.
        </p>
      </div>
    </div>
  </section>
);

const Contact = () => (
  <section id="contact" className="section section-contact">
    <div className="section-inner">
      <h2 className="section-title section-title-light">Let's Work Together</h2>
      <p className="contact-sub">Have a project in mind? I'd love to hear about it.</p>
      <a
        href="mailto:contact@mccarthysoftwaresolutions.com"
        className="contact-email"
      >
        contact@mccarthysoftwaresolutions.com
      </a>
      <div className="contact-btn-wrap">
        <a href="mailto:contact@mccarthysoftwaresolutions.com" className="btn btn-light">
          Send an Email
        </a>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="footer">
    <p>&copy; 2025 McCarthy Software Solutions Limited. All rights reserved.</p>
  </footer>
);

const App = () => (
  <div className="app">
    <Nav />
    <Hero />
    <About />
    <Services />
    <TechStack />
    <Portfolio />
    <Contact />
    <Footer />
  </div>
);

export default App;
